import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Apple, Smartphone, Check, Sparkles, Brain, Users, ShieldCheck,
  BarChart3, Heart, Bell, MessageCircle, LifeBuoy,
} from 'lucide-react';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { safeJsonLd } from '../utils/seo';
import '../styles/pages/SubscriptionPage.css';

const PLANS = [
  {
    name: 'Free',
    tagline: 'Comece agora, sem cartão',
    price: 'R$ 0',
    period: 'para sempre',
    features: [
      'Acesso a qualquer programa',
      'Contador de dias e diário emocional',
      'Comunidade base anônima',
      'Protocolo de emergência (panic button)',
      'Conteúdo essencial',
    ],
    cta: 'Baixar app grátis',
  },
  {
    name: 'Premium Mensal',
    tagline: 'Tudo liberado, mês a mês',
    price: 'R$ 29,90',
    period: 'por mês',
    features: [
      'Tudo do Free',
      'Conteúdo completo (vídeos, áudios, jornadas)',
      'IA Companion 24/7 com privacidade',
      'Comunidade premium e mentoria por pares',
      'Relatórios avançados de progresso',
      'Notificações personalizadas e check-ins guiados',
    ],
    cta: 'Assinar dentro do app',
  },
  {
    name: 'Premium Anual',
    tagline: 'Economize 33% pagando 12 meses',
    price: 'R$ 239,90',
    period: 'por ano',
    monthlyEquivalent: 'Equivalente a R$ 19,99/mês',
    highlighted: true,
    features: [
      'Tudo do Premium Mensal',
      '12 meses de acesso completo',
      'Suporte prioritário',
      'Conteúdos exclusivos de membros anuais',
      'Garantia de preço — sem reajustes no período',
    ],
    cta: 'Assinar dentro do app',
  },
];

const TRUST_HIGHLIGHTS = [
  { icon: <Brain      size={22} />, title: 'Baseado em ciência',     desc: 'Programas estruturados em neurociência comportamental e psicologia clínica.' },
  { icon: <ShieldCheck size={22} />, title: 'Privacidade absoluta',  desc: 'Anonimato total. Criptografia ponta a ponta. Conformidade LGPD/GDPR.' },
  { icon: <Users      size={22} />, title: 'Comunidade 24/7',        desc: 'Grupos blindados de pessoas que entendem exatamente o que você sente.' },
  { icon: <BarChart3  size={22} />, title: 'Progresso visível',      desc: 'Cada streak, recaída e ganho mapeado em métricas que motivam de verdade.' },
];

const APP_FEATURES = [
  { icon: <Heart         size={20} />, title: 'Check-ins diários',          desc: 'Mapeie estado emocional, gatilhos e ganhos.' },
  { icon: <Bell          size={20} />, title: 'Lembretes inteligentes',      desc: 'No horário certo, com o tom que motiva você.' },
  { icon: <Sparkles      size={20} />, title: 'Companion IA',                desc: 'Conversa empática sempre que o gatilho bater.' },
  { icon: <MessageCircle size={20} />, title: 'Comunidade anônima',          desc: 'Compartilhe sem expor sua identidade real.' },
  { icon: <LifeBuoy      size={20} />, title: 'Botão de emergência',         desc: 'Plano de ação imediato em 1 toque.' },
  { icon: <BarChart3     size={20} />, title: 'Relatórios semanais',         desc: 'Veja sua evolução, dia após dia.' },
];

const FAQ = [
  {
    q: 'Os planos pagos já estão disponíveis?',
    a: 'O app está em fase final antes da publicação na App Store e Google Play. As assinaturas pagas serão liberadas no lançamento oficial. Enquanto isso, você já pode criar sua conta gratuita pelo site ou aguardar o app — todo o progresso fica salvo na sua conta.',
  },
  {
    q: 'Quanto vai custar?',
    a: 'Premium Mensal por R$ 29,90 e Premium Anual por R$ 239,90 (≈ R$ 19,99/mês, economia de 33%). Os preços podem variar conforme conversão das lojas Apple/Google e promoções de lançamento.',
  },
  {
    q: 'Como cancelo a assinatura?',
    a: 'Direto pela Apple App Store ou Google Play, em "Assinaturas", a qualquer momento. Detalhes completos em Termos de Uso, seção 6.',
  },
  {
    q: 'Posso pedir reembolso?',
    a: 'Sim. Consumidores no Brasil têm 7 dias de arrependimento (CDC art. 49). Reembolsos via App Store/Google Play seguem políticas dessas plataformas. Veja a seção 7 dos Termos.',
  },
  {
    q: 'Quero ser avisado quando o app for lançado',
    a: 'Envie um e-mail para suporte@lifyup.com.br com o assunto "Notificar lançamento" e te avisaremos assim que o app estiver disponível na sua loja.',
  },
  {
    q: 'É seguro? Vocês veem o que escrevo?',
    a: 'Conteúdo pessoal (diário, registros, mensagens privadas) é criptografado. Equipe técnica não acessa esses dados, exceto em situações de risco de vida ou exigência legal. Veja a Política de Privacidade.',
  },
];

const APP_STORE_URL = '#'; // TODO: substituir pela URL real após publicação
const PLAY_STORE_URL = '#'; // TODO: substituir pela URL real após publicação

const SubscriptionPage = () => {
  const navigate = useNavigate();

  const handleStoreClick = (e, store) => {
    e.preventDefault();
    alert(
      `O app ainda não foi publicado na ${store}. Estamos finalizando o processo de submissão.\n\n` +
      `Envie um e-mail para suporte@lifyup.com.br com o assunto "Notificar lançamento" e te avisaremos assim que estiver disponível.`,
    );
  };

  return (
    <>
      <Helmet>
        <title>Planos e Download — LifeUp</title>
        <meta
          name="description"
          content="Conheça os planos do LifeUp e baixe o app na App Store ou Google Play. Comece grátis ou desbloqueie o Premium dentro do aplicativo."
        />
        <link rel="canonical" href="https://lifyup.com.br/assinar" />
        <meta property="og:title" content="Planos e Download — LifeUp" />
        <meta property="og:description" content="Programas de recuperação comportamental. Comece grátis. Premium dentro do app." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lifyup.com.br/assinar" />
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'LifeUp Premium',
          description: 'Assinatura premium da plataforma LifeUp — programas de transformação de hábitos.',
          brand: { '@type': 'Brand', name: 'LifeUp' },
          offers: [
            { '@type': 'Offer', name: 'Free',           price: '0',      priceCurrency: 'BRL', availability: 'https://schema.org/InStock' },
            { '@type': 'Offer', name: 'Premium Mensal', price: '29.90',  priceCurrency: 'BRL', availability: 'https://schema.org/PreOrder' },
            { '@type': 'Offer', name: 'Premium Anual',  price: '239.90', priceCurrency: 'BRL', availability: 'https://schema.org/PreOrder' },
          ],
        })}</script>
      </Helmet>

      <div className="sub-page">
        <Header />

        <main>
          {/* ===== HERO ===== */}
          <section className="sub-hero">
            <div className="container sub-hero__inner">
              <div className="sub-hero__copy">
                <span className="sub-hero__eyebrow">Em breve nas lojas</span>
                <h1 className="sub-hero__title">
                  Sua jornada de recuperação <span className="sub-hero__accent">começa no app</span>.
                </h1>
                <p className="sub-hero__subtitle">
                  Programas estruturados, comunidade anônima, IA Companion e protocolo de emergência —
                  tudo na palma da mão. Baixe o LifeUp e comece grátis hoje mesmo.
                </p>

                <div className="sub-hero__stores">
                  <a
                    href={APP_STORE_URL}
                    onClick={(e) => handleStoreClick(e, 'App Store')}
                    className="sub-store-btn"
                    aria-label="Baixar na App Store (em breve)"
                  >
                    <Apple size={24} />
                    <span className="sub-store-btn__text">
                      <small>Em breve na</small>
                      <strong>App Store</strong>
                    </span>
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    onClick={(e) => handleStoreClick(e, 'Google Play')}
                    className="sub-store-btn"
                    aria-label="Baixar no Google Play (em breve)"
                  >
                    <Smartphone size={24} />
                    <span className="sub-store-btn__text">
                      <small>Em breve no</small>
                      <strong>Google Play</strong>
                    </span>
                  </a>
                </div>

                <p className="sub-hero__notice">
                  Plataforma em fase final pré-lançamento. As assinaturas pagas serão liberadas
                  diretamente no app. Quer ser avisado?{' '}
                  <a href="mailto:suporte@lifyup.com.br?subject=Notificar%20lan%C3%A7amento">
                    Receber aviso por e-mail
                  </a>.
                </p>
              </div>

              <div className="sub-hero__visual">
                <img
                  src="/assets/images/app-mockup.svg"
                  alt="Pré-visualização do app LifeUp"
                  loading="eager"
                />
              </div>
            </div>
          </section>

          {/* ===== TRUST BAND ===== */}
          <section className="sub-trust">
            <div className="container">
              <div className="sub-trust__grid">
                {TRUST_HIGHLIGHTS.map((t, i) => (
                  <div key={i} className="sub-trust__card">
                    <div className="sub-trust__icon">{t.icon}</div>
                    <h3>{t.title}</h3>
                    <p>{t.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== PLANS ===== */}
          <section className="sub-plans" id="plans">
            <div className="container">
              <header className="sub-section__header">
                <span className="sub-eyebrow">Planos</span>
                <h2>Preços pensados para o longo prazo</h2>
                <p>
                  Comece grátis. Quando estiver pronto para acelerar sua jornada, desbloqueie o Premium
                  dentro do app. Assine, pause ou cancele a qualquer momento pela própria loja.
                </p>
              </header>

              <div className="sub-plans__grid">
                {PLANS.map((plan) => (
                  <article
                    key={plan.name}
                    className={`sub-plan-card${plan.highlighted ? ' is-featured' : ''}`}
                  >
                    {plan.highlighted && <span className="sub-plan-card__badge">Melhor custo-benefício</span>}
                    <header className="sub-plan-card__head">
                      <h3>{plan.name}</h3>
                      <p className="sub-plan-card__tagline">{plan.tagline}</p>
                      <div className="sub-plan-card__price">
                        <span className="sub-plan-card__amount">{plan.price}</span>
                        <span className="sub-plan-card__period">{plan.period}</span>
                      </div>
                      {plan.monthlyEquivalent && (
                        <p className="sub-plan-card__equiv">{plan.monthlyEquivalent}</p>
                      )}
                    </header>

                    <ul className="sub-plan-card__features">
                      {plan.features.map((f) => (
                        <li key={f}>
                          <Check size={16} /> <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      className={`sub-plan-card__cta${plan.highlighted ? ' is-primary' : ''}`}
                      onClick={(e) => handleStoreClick(e, 'App Store ou Google Play')}
                    >
                      {plan.cta}
                    </button>
                  </article>
                ))}
              </div>

              <p className="sub-plans__legal">
                Pagamentos processados pela <strong>Apple App Store</strong> e <strong>Google Play</strong>.
                Renovação automática até cancelamento. Veja os{' '}
                <a href="/termos">Termos de Uso</a> e a{' '}
                <a href="/privacidade">Política de Privacidade</a>.
              </p>
            </div>
          </section>

          {/* ===== FEATURES ===== */}
          <section className="sub-features">
            <div className="container">
              <header className="sub-section__header">
                <span className="sub-eyebrow">No app você encontra</span>
                <h2>Tudo que você precisa em um único lugar</h2>
              </header>
              <div className="sub-features__grid">
                {APP_FEATURES.map((f) => (
                  <div key={f.title} className="sub-feature">
                    <div className="sub-feature__icon">{f.icon}</div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className="sub-faq">
            <div className="container">
              <header className="sub-section__header">
                <span className="sub-eyebrow">Perguntas frequentes</span>
                <h2>Tudo o que você precisa saber</h2>
              </header>
              <div className="sub-faq__list">
                {FAQ.map((item, i) => (
                  <details key={i} className="sub-faq__item">
                    <summary>{item.q}</summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FINAL CTA ===== */}
          <section className="sub-final-cta">
            <div className="container">
              <div className="sub-final-cta__box">
                <h2>Pronto para começar?</h2>
                <p>
                  Baixe o LifeUp e dê o primeiro passo. Gratuito para sempre, Premium quando você quiser.
                </p>
                <div className="sub-hero__stores">
                  <a
                    href={APP_STORE_URL}
                    onClick={(e) => handleStoreClick(e, 'App Store')}
                    className="sub-store-btn"
                  >
                    <Apple size={24} />
                    <span className="sub-store-btn__text">
                      <small>Em breve na</small>
                      <strong>App Store</strong>
                    </span>
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    onClick={(e) => handleStoreClick(e, 'Google Play')}
                    className="sub-store-btn"
                  >
                    <Smartphone size={24} />
                    <span className="sub-store-btn__text">
                      <small>Em breve no</small>
                      <strong>Google Play</strong>
                    </span>
                  </a>
                </div>
                <button
                  type="button"
                  className="sub-final-cta__secondary"
                  onClick={() => navigate('/')}
                >
                  Conhecer os programas primeiro
                </button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SubscriptionPage;
